import styled, { css } from 'styled-components';
import { Heading4Mixin, SitemapMixin, BodyMixin } from '@qover/typography';
import { FormattedMarkdown } from '@qover/react-intl';
import {
  PrimaryBaseButtonMixin,
  PrimaryGhostBaseButtonMixin,
} from '@qover/molecules';
import { Heading5Mixin } from '@qover/typography';
import {
  DocumentIconMixin,
  TheftIconMixin,
  TheftAssistanceIconMixin,
  TheftDamageAssistanceIconMixin,
  MinusIconMixin,
  PlusIconMixin,
} from './BikeIcons';

export const Container = styled.div`
  background-color: ${props => props.theme.colors.cardBackground()};
  margin-top: 40px;
`;

export const Details = styled.details<{ onToggle: any }>`
  border-top: solid 1px ${props => props.theme.colors.textQuote(0.1)};
  color: ${props => props.theme.colors.textQuote()};
  flex: 1;
  margin: 0 auto;
  max-width: 800px;
  padding: 20px 40px;
  width: 100%;

  @media (max-width: ${props => props.theme.breakpoints.maxWidth.S}) {
    padding: 20px;
  }

  & > p {
    transition: max-height 0.5s linear;
    overflow: hidden;
  }
  &:not([open]) > p {
    max-height: 0;
  }
  &[open] > p {
    max-height: 1000px;
  }
  &[open] summary > div:after {
    transform: rotate(-45deg);
  }

  &[open] summary:focus > div:after {
    box-shadow: -2px 2px 10px 2px rgba(0, 0, 0, 0.1);
  }
  &[open] summary {
    padding-bottom: 20px;
  }
  &[open] summary:after {
    ${MinusIconMixin};
    position: absolute;
    right: 0;
    top: 0;
  }
`;

export const AnswerParagraph = styled(FormattedMarkdown)`
  ${BodyMixin};

  &.list-col ul {
    padding-left: 20px;
  }

  /* Adjust line-height for mobile devices */
  li {
    line-height: normal;
  }

  table {
    width: 100%;
    margin-bottom: 20px;
    ${SitemapMixin};
  }
  thead {
    display: none;
  }
  tbody tr:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.colors.textQuote(0.1)};
  }
  tbody td:not(:first-child) {
    text-align: right;
    white-space: nowrap;
  }
  h5 {
    ${Heading5Mixin}
    margin: 40px 0 20px;
  }
  h5:first-of-type {
    margin-top: 0;
  }
  strong {
    font-weight: bold;
  }
  p {
    ${BodyMixin};
    margin-top: 0;
    line-height: normal;
  }

  a {
    font-weight: bold;
  }

  @media (min-width: ${props => props.theme.breakpoints.minWidth.S}) {
    &.list-col ul {
      padding-left: 30px;
    }

    table {
      width: 80%;
    }
    &.list-col-2 ul {
      column-count: 2;
      margin-top: 20px;
    }
    &.list-col-2 ul li {
      break-inside: avoid;
      line-height: 1.3;
      padding: 0 30px 14px 5px;
    }
  }
`;

export const DocumentsListItem = styled.li`
  ${DocumentIconMixin};
  background-size: 16px 20px;
  background-position: left top;
  padding-bottom: 16px;
  padding-left: 28px;

  p {
    margin: 0;
  }
`;

export const Documents = styled(AnswerParagraph)`
  ul {
    list-style: none;
    margin-top: 40px;
  }

  div {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: '. .';
    grid-column-gap: 60px;
    margin-top: 40px;
    p {
      flex: 1%;
    }
    a:first-of-type {
      margin-right: 60px;
    }
    a {
      ${PrimaryBaseButtonMixin};
      display: inline-block;
    }
    h5 {
      margin-top: 0;
    }
  }
`;

export const Contact = styled(AnswerParagraph)`
  .buttons {
    margin: 20px 0;
  }
  .buttons a {
    ${PrimaryGhostBaseButtonMixin};
    display: inline-block;
    line-height: 1;
  }
  div + div {
    margin-top: 10px;
  }
`;

const WarrantiesIconMixin = css`
  background-size: 40px 40px;
  background-position: left top 4px;
  background-repeat: no-repeat;
  padding-left: 60px;
`;

export const Warranties = styled(AnswerParagraph)`
  color: currentColor;
  line-height: normal;
  list-style: none;

  > li {
    :first-child {
      ${TheftAssistanceIconMixin()};
      ${WarrantiesIconMixin};
    }
    :nth-child(2) {
      ${TheftIconMixin()};
      ${WarrantiesIconMixin};
    }
    :nth-child(2) {
      ${TheftIconMixin()};
      ${WarrantiesIconMixin};
    }
    :nth-child(3) {
      ${TheftDamageAssistanceIconMixin()};
      ${WarrantiesIconMixin};
    }
    :nth-child(3) {
      ${TheftDamageAssistanceIconMixin()};
      ${WarrantiesIconMixin};
    }
  }
`;

export const Summary = styled.summary`
  ${Heading4Mixin};
  cursor: pointer;
  font-weight: bold;
  list-style-type: none;
  padding-right: 20px;
  position: relative;
  outline: none;

  &:before {
    content: '';
  }

  &.has-content {
    cursor: pointer;
  }

  &::-webkit-details-marker {
    display: none;
  }

  span {
    display: block;
  }

  :after {
    ${PlusIconMixin};
    position: absolute;
    right: 0;
    top: 0;
  }
`;
